import * as React from 'react'
import { PackageWholesaleDetails, PackageWholesaleProduct } from '@modules/package-wholesale/models'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { PackageWholesaleFormInput } from '@modules/package-wholesale/details'
import { PackageWholesaleGastroCardVariantPredefined } from '@modules/package-wholesale/details/gastro-card/package-wholesale-gastro-card-variant-predefined'
import { PackageWholesaleGastroCardVariantCustom } from '@modules/package-wholesale/details/gastro-card/package-wholesale-gastro-card-variant-custom'
import { asDecimal, sumDecimalArray } from '@helpers/utils'
import { GastroCardPrices } from '@modules/package-wholesale/details/gastro-card/gastro-card-prices'
import { GastroProduct } from '@modules/gastro-generic/models'

export type CustomAmount = 'products.1.amount'

const getCustomAmountKey = index => `products.${index}.amount` as CustomAmount

interface Props {
  packageWholesaleDetails: PackageWholesaleDetails
  gastroProduct: GastroProduct | null
}

export const PackageWholesaleGastroCardCardsSection = ({
  packageWholesaleDetails,
  gastroProduct,
}: Props): JSX.Element => {
  const productKind = gastroProduct?.kind === 'normal' ? 'gastro_card' : gastroProduct?.kind
  const gastroCardProducts = packageWholesaleDetails.products.filter(product => product.kind === productKind)

  const { control, getValues } = useFormContext<PackageWholesaleFormInput>()
  const { fields: products, append, remove } = useFieldArray({ control: control, name: 'products' })

  const addedProducts = useWatch({ control, name: 'products' })
  const isProductAdded = product => addedProducts.some(el => el.product.id === product.id)

  const { predefined, custom } = React.useMemo(
    () =>
      gastroCardProducts.reduce(
        (acc, curr) =>
          curr.price_brutto
            ? { predefined: [...acc.predefined, curr], custom: acc.custom }
            : { predefined: acc.predefined, custom: [...acc.custom, curr] },
        { predefined: [], custom: [] },
      ),
    [gastroCardProducts],
  )

  const handleGastroCardClick = (product: PackageWholesaleProduct) => () => {
    if (isProductAdded(product)) {
      remove(products.findIndex(el => el.product.id === product.id))
      return
    }

    const customProductPrice = getValues(getCustomAmountKey(getArrayIndex(product)))

    append({
      product,
      amount: product.price_brutto ? asDecimal(product?.price_brutto ?? 0).toNumber() : customProductPrice ?? 0,
      active: true,
      with_zoo_borysew_tickets: false,
      options: { zoo_borysew: false },
    })
  }

  const price = React.useMemo(
    () =>
      sumDecimalArray(
        addedProducts.filter(el => el.product.kind === productKind).map(el => asDecimal(el.amount)),
      ).toString(),
    [addedProducts],
  )

  const multipliedPrice = asDecimal(price)
    .mul(gastroProduct?.multiplier || 1)
    .toString()

  const getArrayIndex = product => products.findIndex(el => el.product.id === product.id)

  return (
    <>
      <div className="col-sm-10 col-12">
        <div>
          <h5 className="text-secondary font-xxl my-3 fw-bold text-center text-sm-start">
            {`Oferujemy vouchery ${gastroProduct?.product_name} `}
            <span className="underline-shadow text-gold text-nowrap">w specjalnych cenach:</span>
          </h5>
          <strong className="text-primary mb-1 d-block">Wybierz rodzaj vouchera:</strong>
          {predefined.map(product => (
            <PackageWholesaleGastroCardVariantPredefined
              key={product.id}
              product={product}
              isActive={isProductAdded(product)}
              onCardClick={handleGastroCardClick(product)}
            />
          ))}

          {!!custom.length && (
            <strong className="mt-4 mb-1 d-block text-darker-gray">lub wpisz własną kwotę vouchera:</strong>
          )}
          {custom.map(product => (
            <PackageWholesaleGastroCardVariantCustom
              key={product.id}
              inputName={getCustomAmountKey(getArrayIndex(product))}
              isActive={isProductAdded(product)}
              onCardClick={handleGastroCardClick(product)}
              gastroProduct={gastroProduct}
            />
          ))}
        </div>
      </div>
      <GastroCardPrices basicPrice={price} bonusPrice={multipliedPrice} />
    </>
  )
}
